<template>
  <div id="app">
    <div v-if="isDev" class="env-banner">DESARROLLO — los datos de este sitio son una copia y no afectan producción</div>
    <nav class="navbar" v-if="isAuthenticated">
      <div class="container">
        <router-link to="/" class="ip-brand" @click="showMenu = false">
          <span class="ip-logo">ip</span>
        </router-link>
        <button class="hamburger" @click="showMenu = !showMenu" aria-label="Menú" v-if="isMobile">
          <span class="hamburger-bar"></span>
          <span class="hamburger-bar"></span>
          <span class="hamburger-bar"></span>
        </button>
        <div class="nav-links" :class="{ 'show-mobile': showMenu || !isMobile }" @click="showMenu = false">
          <router-link v-if="puedeCategoria('csb')" to="/csb" class="nav-link" :class="{ 'manual-active': isCSBActive }">CSB</router-link>
          <router-link v-if="puedeCategoria('arriendos')" to="/arriendos" class="nav-link" :class="{ 'manual-active': isArriendosActive }">Arriendos</router-link>
          <router-link v-if="puedeCategoria('empresas')" to="/empresas" class="nav-link" :class="{ 'manual-active': isEmpresasActive }">Empresas</router-link>
          <router-link v-if="puedeCategoria('sangre')" to="/sangre" class="nav-link" :class="{ 'manual-active': isSangreActive }">Sangre</router-link>
          <router-link v-if="puedeCategoria('pjud')" to="/pjud" class="nav-link" :class="{ 'manual-active': isPjudActive }">PJUD</router-link>
          <router-link v-if="puedeCategoria('seguros')" to="/seguros" class="nav-link" :class="{ 'manual-active': isSegurosActive }">Seguros</router-link>
          <div class="nav-session">
            <span class="nav-user" v-if="username">{{ username }}</span>
            <router-link v-if="esAdmin" to="/admin/permisos" class="nav-icon" :class="{ 'manual-active': isAdminActive }" title="Administración" aria-label="Administración">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              <span class="nav-icon-label">Administración</span>
            </router-link>
            <button type="button" class="nav-icon" @click="logout" title="Cerrar sesión" aria-label="Cerrar sesión">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              <span class="nav-icon-label">Salir</span>
            </button>
          </div>
        </div>
      </div>
    </nav>

    <div class="main-layout">
      <aside v-if="showSidebar" class="sidebar">
        <ul>
          <li v-for="endpoint in endpoints" :key="endpoint.path">
            <router-link :to="endpoint.path">{{ endpoint.name }}</router-link>
          </li>
        </ul>
      </aside>

      <main class="content">
        <router-view @toggle-sidebar="toggleSidebar"/>
      </main>
    </div>

    <footer class="footer">
      <div class="container">
        <span>&copy; {{ currentYear }} ip</span>
        <nav class="footer-links" v-if="isAuthenticated">
          <router-link v-if="puedeCategoria('indicadores')" to="/indicadores">Indicadores económicos</router-link>
        </nav>
        <span v-if="appVersion" class="app-version">Argos {{ appVersion }}</span>
      </div>
    </footer>
  </div>
</template>

<script>
import { computed, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

export default {
  name: 'App',
  setup() {
    const store = useStore();
    const isAuthenticated = computed(() => store.getters['auth/isAuthenticated']);
    const puedeCategoria = (c) => store.getters['auth/puedeCategoria'](c);
    const esAdmin = computed(() => store.getters['auth/esAdmin']);
    const username = computed(() => store.getters['auth/user']?.username);
    const logout = () => {
      showMenu.value = false;
      store.dispatch('auth/logout');
    };
    const route = useRoute();
    const showSidebar = ref(false);
    const endpoints = ref([]);
    const showMenu = ref(false);
    const isMobile = ref(false);

    const checkMobile = () => {
      isMobile.value = window.innerWidth <= 800;
      if (!isMobile.value) showMenu.value = false;
    };
    onMounted(() => {
      checkMobile();
      window.addEventListener('resize', checkMobile);
    });

    const toggleSidebar = (appEndpoints) => {
      showSidebar.value = !!appEndpoints;
      endpoints.value = appEndpoints || [];
    };

    const currentYear = computed(() => new Date().getFullYear());
    const isCSBActive = computed(() => route.path.startsWith('/csb'));
    const isArriendosActive = computed(() => route.path.startsWith('/arriendos'));
    const isEmpresasActive = computed(() => route.path.startsWith('/empresas'));
    const isSangreActive = computed(() => route.path.startsWith('/sangre'));
    const isPjudActive = computed(() => route.path.startsWith('/pjud'));
    const isSegurosActive = computed(() => route.path.startsWith('/seguros'));
    const isAdminActive = computed(() => route.path.startsWith('/admin'));
    const isDev = window.location.hostname.includes('-dev') || window.location.hostname === 'localhost';
    const appVersion = process.env.VUE_APP_VERSION || '';

    return {
      isDev,
      appVersion,
      showSidebar,
      endpoints,
      toggleSidebar,
      currentYear,
      isCSBActive,
      isArriendosActive,
      isEmpresasActive,
      isSangreActive,
      isPjudActive,
      isSegurosActive,
      isAdminActive,
      showMenu,
      isMobile,
      isAuthenticated,
      puedeCategoria,
      esAdmin,
      username,
      logout
    };
  },
};
</script>

<style lang="scss">
// Reset y estilos globales
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  background-color: #f7fafc;
  color: #2d3748;
  line-height: 1.6;
}

.env-banner {
  background: #d97706;
  color: #fff;
  text-align: center;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 4px 8px;
}

#app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

// Barra de navegación
.navbar {
  background-color: #1a202c;
  color: #fff;
  position: relative;
  z-index: 1100;
  box-shadow: 0 1px 0 rgba(255,255,255,0.06);
  .container {
    display: flex;
    align-items: center;
    height: 60px;
    gap: 2rem;
  }
  .ip-brand {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    text-decoration: none;
  }
  .ip-logo {
    font-family: Georgia, serif;
    font-style: italic;
    font-size: 30px;
    font-weight: bold;
    line-height: 1;
    color: #4299e1;
    letter-spacing: 1px;
  }
  .nav-links {
    display: flex;
    align-items: center;
    flex: 1;
    gap: 0.25rem;
    height: 100%;
  }
  .nav-link {
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 0.9rem;
    color: #a0aec0;
    font-size: 0.95rem;
    font-weight: 500;
    text-decoration: none;
    border-bottom: 3px solid transparent;
    border-top: 3px solid transparent;
    transition: color 0.15s, border-color 0.15s;
    &:hover {
      color: #fff;
    }
    &.router-link-active,
    &.manual-active {
      color: #fff;
      border-bottom-color: #4299e1;
    }
  }
  .nav-session {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-left: auto;
  }
  .nav-user {
    color: #718096;
    font-size: 0.875rem;
    margin-right: 0.5rem;
  }
  .nav-icon {
    display: inline-flex;
    align-items: center;
    gap: 0.4em;
    background: transparent;
    border: none;
    color: #a0aec0;
    font: inherit;
    font-size: 0.875rem;
    padding: 0.45em;
    border-radius: 6px;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.15s, color 0.15s;
    &:hover,
    &:focus-visible {
      background: rgba(255,255,255,0.08);
      color: #fff;
      outline: none;
    }
    &.router-link-active,
    &.manual-active {
      color: #4299e1;
    }
  }
  .nav-icon-label {
    display: none;
  }
  .hamburger {
    display: none;
    background: none;
    border: none;
    flex-direction: column;
    justify-content: center;
    cursor: pointer;
    padding: 0.5em;
    margin-left: auto;
  }
  .hamburger-bar {
    width: 24px;
    height: 2px;
    background: #e2e8f0;
    margin: 3px 0;
    border-radius: 1px;
  }
}

@media (max-width: 800px) {
  .navbar {
    .hamburger {
      display: flex;
    }
    .nav-links {
      display: none;
      flex-direction: column;
      align-items: stretch;
      gap: 0;
      height: auto;
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: #1a202c;
      box-shadow: 0 6px 12px rgba(0,0,0,0.2);
      &.show-mobile {
        display: flex;
      }
    }
    .nav-link {
      height: auto;
      padding: 0.9rem 1.5rem;
      border: none;
      border-left: 3px solid transparent;
      border-bottom: 1px solid rgba(255,255,255,0.06);
      &.router-link-active,
      &.manual-active {
        border-left-color: #4299e1;
        border-bottom-color: rgba(255,255,255,0.06);
      }
    }
    .nav-session {
      margin-left: 0;
      padding: 0.6rem 1.2rem;
      flex-wrap: wrap;
    }
    .nav-user {
      width: 100%;
      margin: 0 0 0.25rem 0.3rem;
    }
    .nav-icon-label {
      display: inline;
    }
  }
}

// Pie de página
.footer {
  background-color: #1a202c;
  color: #a0aec0;
  padding: 1rem 0;
  margin-top: 2rem;
  font-size: 0.8rem;
  .container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem 1.5rem;
  }
  .footer-links {
    display: flex;
    gap: 1.25rem;
    a {
      color: #cbd5e0;
      text-decoration: none;
      &:hover,
      &.router-link-active {
        color: #4299e1;
      }
    }
  }
  .app-version {
    opacity: 0.6;
  }
}

// Utilidades
.error-message {
  padding: 0.75rem;
  margin-bottom: 1rem;
  background-color: #fed7d7;
  color: #c53030;
  border-radius: 4px;
  font-size: 0.875rem;
}

.success-message {
  padding: 0.75rem;
  margin-bottom: 1rem;
  background-color: #c6f6d5;
  color: #2f855a;
  border-radius: 4px;
  font-size: 0.875rem;
}

// Estilos de botones globales
.btn {
  display: inline-block;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.2s;
  &.btn-primary {
    background-color: #4299e1;
    color: white;
    border: none;
    &:hover {
    background-color: #3182ce;
    }
  }
  &.btn-secondary {
    background-color: transparent;
    color: #4299e1;
    border: 1px solid #4299e1;
    &:hover {
      background-color: #ebf8ff;
    }
  }
}
</style>
